import React, { useState, useRef, useEffect } from 'react';

/**
 * Célula editável inline. Suporta text, textarea, select, date, tags, checkbox, email, tel.
 *
 * Props:
 * - value: valor atual (para tags: array de strings; para checkbox: boolean)
 * - onSave(newValue): callback ao salvar (para tags recebe array; para checkbox recebe boolean)
 * - type: 'text' | 'textarea' | 'select' | 'date' | 'tags' | 'checkbox' | 'email' | 'tel'
 * - options: [{value, label}] para type=select
 * - placeholder: texto quando vazio
 * - displayValue: texto a mostrar quando NÃO está em edição (útil para select que mostra label, não value)
 * - colorClass: classes extras aplicadas ao wrapper de display (ex: badge colorido de estágio)
 * - className: classes aplicadas a todo wrapper
 */
export const EditableCell = ({
  value,
  onSave,
  type = 'text',
  options = [],
  placeholder = '—',
  displayValue,
  colorClass = '',
  className = '',
  noTruncate = false,
}) => {
  const [editing, setEditing] = useState(false);
  const initial = type === 'tags'
    ? (Array.isArray(value) ? value.join(', ') : '')
    : (value ?? '');
  const [localValue, setLocalValue] = useState(initial);
  const inputRef = useRef(null);

  useEffect(() => {
    if (type === 'tags') {
      setLocalValue(Array.isArray(value) ? value.join(', ') : '');
    } else {
      setLocalValue(value ?? '');
    }
  }, [value, type]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
      if (type === 'textarea') {
        const el = inputRef.current;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.5) + 'px';
      }
    }
  }, [editing, type]);

  // Checkbox: toggle inline sem estados de edit/display separados.
  // Fica DEPOIS dos hooks (Regra 6 do CLAUDE.md).
  if (type === 'checkbox') {
    return (
      <label className={`inline-flex items-center gap-1 cursor-pointer ${className}`}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onSave(e.target.checked)}
          className="rounded border-slate-300"
        />
      </label>
    );
  }

  const handleSave = () => {
    if (type === 'tags') {
      const arr = (localValue || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const prev = Array.isArray(value) ? value : [];
      const changed = arr.length !== prev.length || arr.some((v, i) => v !== prev[i]);
      if (changed) onSave(arr.length ? arr : null);
      setEditing(false);
      return;
    }
    const finalValue = typeof localValue === 'string' ? localValue.trim() : localValue;
    if (finalValue !== (value ?? '')) {
      onSave(finalValue);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(value ?? '');
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (type === 'textarea') {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    } else {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    }
  };

  // ------- Display mode -------
  if (!editing) {
    let toDisplay;
    if (displayValue !== undefined) {
      toDisplay = displayValue;
    } else if (type === 'select') {
      toDisplay = options.find(o => o.value === value)?.label || placeholder;
    } else if (type === 'tags') {
      toDisplay = Array.isArray(value) && value.length > 0 ? value.join(', ') : placeholder;
    } else {
      toDisplay = value || placeholder;
    }

    const isShowingPlaceholderInIdle = displayValue === undefined && (
      value === null || value === undefined || value === '' ||
      (Array.isArray(value) && value.length === 0)
    );

    return (
      <button
        onClick={() => setEditing(true)}
        className={`text-left ${colorClass} ${className} hover:bg-slate-100 rounded px-1 py-0.5 block w-full ${noTruncate ? 'whitespace-nowrap' : 'truncate'}`}
        title={type === 'textarea' || type === 'tags' ? (toDisplay || '') : undefined}
      >
        {isShowingPlaceholderInIdle
          ? <span className="text-slate-400 italic font-normal">{placeholder}</span>
          : (toDisplay || placeholder)}
      </button>
    );
  }

  // ------- Edit mode -------
  if (type === 'select') {
    return (
      <select
        ref={inputRef}
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
          // Salva imediatamente ao escolher
          if (e.target.value !== (value ?? '')) {
            onSave(e.target.value);
          }
          setEditing(false);
        }}
        onBlur={() => setEditing(false)}
        onKeyDown={handleKeyDown}
        className={`${className} border border-blue-400 rounded px-1 py-0.5 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-500`}
      >
        {options.map(o => (
          <option key={String(o.value)} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  if (type === 'textarea') {
    return (
      <textarea
        ref={inputRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onInput={(e) => {
          e.target.style.height = 'auto';
          e.target.style.height = Math.min(e.target.scrollHeight, window.innerHeight * 0.5) + 'px';
        }}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        rows={3}
        className={`${className} border border-blue-400 rounded px-1.5 py-1 text-xs w-full resize-none overflow-y-auto focus:outline-none focus:ring-1 focus:ring-blue-500`}
        style={{ maxHeight: '50vh' }}
        placeholder={placeholder}
      />
    );
  }

  if (type === 'date') {
    return (
      <input
        ref={inputRef}
        type="date"
        value={localValue || ''}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`${className} border border-blue-400 rounded px-1 py-0.5 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-500`}
      />
    );
  }

  // default: text, email, tel, tags (todos com input nativo, tags é comma-separated string)
  const inputType = type === 'email' ? 'email' : type === 'tel' ? 'tel' : 'text';
  return (
    <input
      ref={inputRef}
      type={inputType}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleSave}
      onKeyDown={handleKeyDown}
      placeholder={type === 'tags' ? 'separar por vírgula' : placeholder}
      className={`${className} border border-blue-400 rounded px-1 py-0.5 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-500`}
    />
  );
};

export default EditableCell;
